import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import moment from 'moment';
import { useQuery } from 'hooks';
import Card, { Number, Description, SeeAll } from './Card';
import { Col } from 'react-bootstrap';

export default function PendingOccurrence({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [daysFromNow, setDaysFromNow] = useState(3);
  const queryParams = useQuery();
  useEffect(() => {
    setDaysFromNow(moment(new Date()).diff(filter.start_date, 'days'));
  }, [filter.start_date]);

  function setQueryParams(filters) {
    const { start_date, end_date } = filters;

    if (start_date) {
      queryParams.set('start_date', start_date);
    }

    if (end_date) {
      queryParams.set('end_date', end_date);
    }
  }

  const fetchPreLoads = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const {
        branches = [],
        tags = [],
        clients = [],
        negotiators = [],
        costCenters = [],
        pcps = [],
        companies = [],
        page = 1,
        start_date,
        end_date,
        occurenceType = [],
        responsibleGroup = [],
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get('occurrence-pending', {
        params: {
          tags: tags?.map(tag => tag.id),
          clients: clients?.map(client => client.id),
          start_date,
          clients: clients?.map(client => client.id),
          start_date,
          negotiators: negotiators?.map(negotiator => negotiator?.id),
          costCenters: costCenters?.map(costCenter => costCenter?.id),
          pcps: pcps?.map(pcp => pcp?.id),
          companies: companies?.map(company => company?.id),
          end_date: end_date || undefined,
          branches: branches?.map(branch => branch?.id),
          occurenceType: occurenceType?.map(type => type?.id),
          responsibleGroup: responsibleGroup?.map(group => group?.id),
          page,
        },
      });

      setPagination(response.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData(oldData => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      // Handle exception
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  return (
    <Card
      title="Ocorrências Pendentes"
      isLoading={isFetching}
      fetchFn={fetchPreLoads}
      onFiltersChanged={fetchPreLoads}
      dashboard={dashboard}
      name={name}
      id={id}
      excludeFilterByBranchOffice={true}
      excludeFilterTypeOccurrence={false}
      excludeFilterResponsibleGroup={false}
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Data criação
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Viagem
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Status da viagem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Cliente
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Col style={{ display: 'flex', gap: '0.1rem', flexDirection: 'column', padding: '0' }}>
                    <Text weight="500" color="dark">
                      Tipo ocorrência
                    </Text>
                    <Text weight="500" color="dark">
                      Grupo responsável
                    </Text>
                  </Col>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Criado por:
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.last?.data?.map(occurence => (
                <tr key={occurence?.occurence_id}>
                  <th>
                    <Text type="regular" color="gray" className="text-nowrap">
                      {formatDateTime(occurence.occurrence_created_at)}
                    </Text>
                  </th>
                  <th>
                    {
                      <Link to={`/viagens/${occurence.travel_id}`} target="_blank">
                        {occurence?.travel_id}
                      </Link>
                    }
                  </th>
                  <th className="text-nowrap">
                    <Text type="regular" color="gray">
                      {occurence?.travel_status_name}
                    </Text>
                  </th>
                  <th >
                    <Text type="regular" color="gray">
                      {occurence?.client_info}
                    </Text>
                  </th>
                  <th className="text-nowrap" style={{ display: 'flex', gap: '0.2rem', flexDirection: 'column' }}>
                    <Text type="regular" color="gray">
                      {occurence?.occurrence_type_name}
                    </Text>
                    <Text type="regular" color="gray">
                      {occurence?.occurrence_groups}
                    </Text>
                  </th>
                  <th className="text-nowrap">
                    <Text type="regular" color="gray">
                      {occurence?.user_created_name}
                    </Text>
                  </th>
                </tr>
              ))}
            </tbody>
          </Table >
          {pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchPreLoads({
                    page: currentPage + 1,
                    tags: filter?.tags,
                    clients: filter?.clients,
                    start_date: filter?.start_date,
                    end_date: filter?.end_date,
                    costCenters: filter?.costCenters,
                    pcps: filter?.pcps,
                    negotiators: filter?.negotiators,
                    companies: filter?.companies,
                    preLoadStatus: filter?.preLoadStatus,
                    filterByPeriod: filter?.filterByPeriod,
                    branches: filter?.branches,
                    occurenceType: filter?.occurenceType,
                    responsibleGroup: filter?.responsibleGroup,
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="warning">{data?.total}</Number>
          <Description>
            Ocorrências pendentes
            {daysFromNow > 1 && daysFromNow < 61
                ? ` nos últimos ${daysFromNow} dias`
                : ` nos últimos ${filter.start_date} dias`}
          </Description>
        </div>
      }
    />
  );
}
